import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const STATUS_COLORS = {
  TRIAL:     { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  ACTIVE:    { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  PAST_DUE:  { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  GRACE:     { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  EXPIRED:   { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  CANCELLED: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  PAID:      { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  DRAFT:     { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  ISSUED:    { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  OVERDUE:   { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  SUCCESS:   { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  FAILED:    { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  PENDING:   { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' }
};

const fmtINR = (n) => {
  if (n == null) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.EXPIRED;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, textTransform: 'uppercase', letterSpacing: 0.3
    }}>
      {status}
    </span>
  );
}

function SubscriptionDetail({ token, subscriptionId, onBack }) {
  const { isMobile } = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sub, setSub] = useState(null);
  const [tab, setTab] = useState('modules');

  // Modal state
  const [showExtend, setShowExtend] = useState(false);
  const [extendDays, setExtendDays] = useState('');
  const [extendLoading, setExtendLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    ...extra
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/subscription/admin/subscriptions/${subscriptionId}`, { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable');
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load');
      setSub(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && subscriptionId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, subscriptionId]);

  const doExtend = async () => {
    if (!extendDays || Number(extendDays) <= 0) return;
    setExtendLoading(true);
    setActionMsg('');
    try {
      const res = await fetch(`/api/v1/subscription/admin/subscriptions/${subscriptionId}/extend`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ days: Number(extendDays) })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to extend');
      setActionMsg(`Extended by ${extendDays} days.`);
      setShowExtend(false);
      setExtendDays('');
      load();
    } catch (e) {
      setActionMsg(e.message);
    } finally {
      setExtendLoading(false);
    }
  };

  const doCancel = async () => {
    if (!cancelReason.trim()) return;
    setCancelLoading(true);
    setActionMsg('');
    try {
      const res = await fetch(`/api/v1/subscription/admin/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reason: cancelReason.trim() })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to cancel');
      setActionMsg('Subscription cancelled.');
      setShowCancel(false);
      setCancelReason('');
      load();
    } catch (e) {
      setActionMsg(e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const thStyle = {
    padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280',
    borderBottom: '2px solid var(--theme-border, #e5e7eb)',
    background: 'var(--theme-surface, #f9fafb)', whiteSpace: 'nowrap'
  };
  const tdStyle = {
    padding: '8px 12px', fontSize: 13, borderBottom: '1px solid var(--theme-border, #e5e7eb)',
    color: 'var(--theme-text, #374151)'
  };

  const btnStyle = (color = 'var(--theme-primary, #2563eb)') => ({
    padding: '7px 16px', borderRadius: 6, border: `1px solid ${color}`,
    background: 'transparent', color, fontSize: 13, cursor: 'pointer', fontWeight: 500
  });

  const tabs = ['modules', 'invoices', 'payments', 'events'];

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 9999
  };

  const modalStyle = {
    background: 'var(--theme-surface, #fff)', borderRadius: 10,
    padding: isMobile ? 20 : 28, width: isMobile ? '90%' : 400,
    maxWidth: '95vw', boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          padding: '6px 14px', borderRadius: 6, border: '1px solid var(--theme-border, #d1d5db)',
          background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)',
          fontSize: 13, cursor: 'pointer', marginBottom: 16
        }}
      >
        &larr; Back to List
      </button>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13
        }}>
          {error}
        </div>
      )}

      {actionMsg && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#f0fdf4',
          border: '1px solid #bbf7d0', borderRadius: 6, color: '#16a34a', fontSize: 13
        }}>
          {actionMsg}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>}

      {!loading && sub && (
        <>
          {/* Header card */}
          <div style={{
            background: 'var(--theme-surface, #fff)',
            border: '1px solid var(--theme-border, #e5e7eb)',
            borderRadius: 10, padding: isMobile ? 16 : 24, marginBottom: 20
          }}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
              alignItems: 'flex-start', gap: 12, marginBottom: 16
            }}>
              <div>
                <h2 style={{ margin: '0 0 6px', fontSize: isMobile ? 18 : 22, color: 'var(--theme-text, #1f2937)' }}>
                  {sub.chain_name || sub.chain_id || 'Subscription'}
                </h2>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {sub.property_name || sub.property_id || ''} {sub.scope ? `| Scope: ${sub.scope}` : ''}
                </div>
              </div>
              <StatusBadge status={sub.status} />
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: 12, fontSize: 13
            }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Plan</div>
                <div style={{ fontWeight: 600, color: 'var(--theme-text, #1f2937)' }}>{sub.plan_name || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Billing Period</div>
                <div style={{ fontWeight: 600, color: 'var(--theme-text, #1f2937)' }}>{sub.billing_period || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Trial End</div>
                <div style={{ fontWeight: 600, color: 'var(--theme-text, #1f2937)' }}>{fmtDate(sub.trial_end_date)}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', marginBottom: 2 }}>Period End</div>
                <div style={{ fontWeight: 600, color: 'var(--theme-text, #1f2937)' }}>{fmtDate(sub.current_period_end)}</div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <button onClick={() => setShowExtend(true)} style={btnStyle()}>
                Extend
              </button>
              <button onClick={() => setShowCancel(true)} style={btnStyle('#dc2626')}>
                Cancel Subscription
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 0, borderBottom: '2px solid var(--theme-border, #e5e7eb)',
            marginBottom: 16, overflowX: 'auto'
          }}>
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '10px 20px', border: 'none', background: 'transparent',
                  fontSize: 13, fontWeight: tab === t ? 600 : 400, cursor: 'pointer',
                  color: tab === t ? 'var(--theme-primary, #2563eb)' : '#6b7280',
                  borderBottom: tab === t ? '2px solid var(--theme-primary, #2563eb)' : '2px solid transparent',
                  marginBottom: -2, textTransform: 'capitalize', whiteSpace: 'nowrap'
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === 'modules' && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Module</th>
                    <th style={thStyle}>Quantity</th>
                    <th style={thStyle}>Unit Price</th>
                    <th style={thStyle}>Total</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.items || []).map((it, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{it.module_name || it.module_code || '-'}</td>
                      <td style={tdStyle}>{it.quantity ?? 1}</td>
                      <td style={tdStyle}>{fmtINR(it.unit_price)}</td>
                      <td style={tdStyle}>{fmtINR(it.total_price || (it.unit_price * (it.quantity || 1)))}</td>
                      <td style={tdStyle}><StatusBadge status={it.status || 'ACTIVE'} /></td>
                    </tr>
                  ))}
                  {(!sub.items || sub.items.length === 0) && (
                    <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', padding: 30, color: '#6b7280' }}>No modules.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'invoices' && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Invoice #</th>
                    <th style={thStyle}>Period</th>
                    <th style={thStyle}>Issue Date</th>
                    <th style={thStyle}>Due Date</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.invoices || []).map((inv, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{inv.invoice_number || inv.id || '-'}</td>
                      <td style={tdStyle}>{fmtDate(inv.period_start)} - {fmtDate(inv.period_end)}</td>
                      <td style={tdStyle}>{fmtDate(inv.issue_date)}</td>
                      <td style={tdStyle}>{fmtDate(inv.due_date)}</td>
                      <td style={tdStyle}>{fmtINR(inv.total_amount)}</td>
                      <td style={tdStyle}><StatusBadge status={inv.status} /></td>
                    </tr>
                  ))}
                  {(!sub.invoices || sub.invoices.length === 0) && (
                    <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: 30, color: '#6b7280' }}>No invoices.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'payments' && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Payment ID</th>
                    <th style={thStyle}>Invoice</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Method</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.payments || []).map((p, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{p.payment_id || p.id || '-'}</td>
                      <td style={tdStyle}>{p.invoice_number || p.invoice_id || '-'}</td>
                      <td style={tdStyle}>{fmtINR(p.amount)}</td>
                      <td style={tdStyle}>{p.payment_method || p.gateway || '-'}</td>
                      <td style={tdStyle}>{fmtDateTime(p.paid_at || p.created_at)}</td>
                      <td style={tdStyle}><StatusBadge status={p.status || 'SUCCESS'} /></td>
                    </tr>
                  ))}
                  {(!sub.payments || sub.payments.length === 0) && (
                    <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: 30, color: '#6b7280' }}>No payments.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'events' && (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Timestamp</th>
                    <th style={thStyle}>Event</th>
                    <th style={thStyle}>Actor</th>
                    <th style={thStyle}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.events || []).map((ev, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{fmtDateTime(ev.created_at || ev.timestamp)}</td>
                      <td style={tdStyle}>{ev.event_type || ev.event || '-'}</td>
                      <td style={tdStyle}>{ev.actor_name || ev.actor || '-'}</td>
                      <td style={{ ...tdStyle, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {typeof ev.details === 'object' ? JSON.stringify(ev.details) : (ev.details || '-')}
                      </td>
                    </tr>
                  ))}
                  {(!sub.events || sub.events.length === 0) && (
                    <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', padding: 30, color: '#6b7280' }}>No events.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Extend Modal */}
      {showExtend && (
        <div style={overlayStyle} onClick={() => setShowExtend(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, color: 'var(--theme-text, #1f2937)' }}>
              Extend Subscription
            </h3>
            <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 6 }}>
              Number of days to extend
            </label>
            <input
              type="number"
              min="1"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              placeholder="e.g. 30"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6,
                border: '1px solid var(--theme-border, #d1d5db)', fontSize: 14,
                marginBottom: 16, boxSizing: 'border-box',
                background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)'
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowExtend(false)} style={btnStyle('#6b7280')}>Cancel</button>
              <button
                onClick={doExtend}
                disabled={extendLoading || !extendDays}
                style={{
                  padding: '7px 20px', borderRadius: 6, border: 'none',
                  background: 'var(--theme-primary, #2563eb)', color: '#fff',
                  fontSize: 13, cursor: extendLoading ? 'default' : 'pointer', fontWeight: 500,
                  opacity: extendLoading || !extendDays ? 0.6 : 1
                }}
              >
                {extendLoading ? 'Extending...' : 'Extend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancel && (
        <div style={overlayStyle} onClick={() => setShowCancel(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, color: '#dc2626' }}>
              Cancel Subscription
            </h3>
            <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 6 }}>
              Reason for cancellation
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Enter reason..."
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6,
                border: '1px solid var(--theme-border, #d1d5db)', fontSize: 14,
                marginBottom: 16, boxSizing: 'border-box', resize: 'vertical',
                background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)'
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCancel(false)} style={btnStyle('#6b7280')}>Close</button>
              <button
                onClick={doCancel}
                disabled={cancelLoading || !cancelReason.trim()}
                style={{
                  padding: '7px 20px', borderRadius: 6, border: 'none',
                  background: '#dc2626', color: '#fff',
                  fontSize: 13, cursor: cancelLoading ? 'default' : 'pointer', fontWeight: 500,
                  opacity: cancelLoading || !cancelReason.trim() ? 0.6 : 1
                }}
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionDetail;
